import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { 
  ArrowLeft, 
  Building, 
  Calendar, 
  DollarSign, 
  MapPin, 
  Clock, 
  FileText,
  Shield,
  AlertCircle,
  CheckCircle,
  Loader2,
  User,
  Lock
} from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import ProposalSubmissionForm from '../../components/gov/ProposalSubmissionForm'
import apiService from '../../services/api'

interface RfqDetails {
  id: string
  title: string
  description: string
  budgetRange: string
  location: string
  deadline: string
  attachments: string[]
  createdAt: string
  GovAgency: {
    id: string
    name: string
    agencyType: string
    website?: string
    contactPerson: string
    email: string
    phone: string
    address: string
    city: string
    state: string
    zipCode: string
    approved: boolean
  }
}

export default function RfqRespondPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [showForm, setShowForm] = useState(false)

  // Fetch RFQ details
  const { data: rfq, isLoading, error } = useQuery<RfqDetails>({
    queryKey: ['public-rfq', id],
    queryFn: async () => {
      const response = await apiService.get(`/api/gov/rfqs/public/${id}`)
      if (typeof response === 'object' && response && 'data' in response) {
        return (response as any).data;
      }
      return undefined;
    },
    enabled: !!id
  })

  // Check if user has already submitted a proposal
  const { data: existingProposal } = useQuery({
    queryKey: ['vendor-proposal', id],
    queryFn: async () => {
      const response = await apiService.get(`/gov/proposals/vendor?rfqId=${id}`)
      if (typeof response === 'object' && response && 'data' in response) {
        return (response as any).data.find((p: any) => p.GovRfq.id === id);
      }
      return undefined;
    },
    enabled: !!user && !!id && (user.role === 'VENDOR' || user.role === 'ENTERPRISE_VENDOR')
  })

  const handleProposalSuccess = () => {
    setShowForm(false)
    navigate('/enterprise/gov-rfqs', { 
      state: { message: 'Proposal submitted successfully!' }
    })
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading RFQ details...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error || !rfq) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">RFQ Not Found</h2>
          <p className="text-gray-600 mb-4">The RFQ you're looking for doesn't exist or may have been removed.</p>
          <Link 
            to="/enterprise/gov-rfqs"
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to RFQ Listings
          </Link>
        </div>
      </div>
    )
  }

  // Check if user is not logged in
  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="text-center">
              <Lock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Login Required</h2>
              <p className="text-gray-600 mb-6">You must be logged in as a vendor to respond to RFQs.</p>
              <div className="flex gap-4 justify-center">
                <Link 
                  to="/login"
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Login
                </Link>
                <Link 
                  to="/enterprise/gov-rfqs"
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Back to RFQs
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Check if user is not a vendor (support both VENDOR and ENTERPRISE_VENDOR)
  const isVendor = user.role === 'VENDOR' || user.role === 'ENTERPRISE_VENDOR';
  if (!isVendor) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="text-center">
              <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Vendor Account Required</h2>
              <p className="text-gray-600 mb-6">Only verified vendors can respond to government RFQs.</p>
              <div className="flex gap-4 justify-center">
                <Link 
                  to="/vendor/register"
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Become a Vendor
                </Link>
                <Link 
                  to="/enterprise/gov-rfqs"
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Back to RFQs
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Check if vendor is not verified
  if (!user.verified) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="text-center">
              <Shield className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Account Verification Required</h2>
              <p className="text-gray-600 mb-6">Your vendor account must be verified before you can respond to RFQs.</p>
              <div className="flex gap-4 justify-center">
                <Link 
                  to="/profile"
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Complete Verification
                </Link>
                <Link 
                  to="/enterprise/gov-rfqs"
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Back to RFQs
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Check if deadline has passed
  const deadlineDate = new Date(rfq.deadline)
  const isDeadlinePassed = deadlineDate < new Date()

  if (isDeadlinePassed) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="text-center">
              <Clock className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Deadline Passed</h2>
              <p className="text-gray-600 mb-6">The deadline for this RFQ has passed and is no longer accepting proposals.</p>
              <Link 
                to="/enterprise/gov-rfqs"
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Browse Other RFQs
              </Link>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Check if user has already submitted a proposal
  if (existingProposal) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="text-center">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-900 mb-2">Proposal Already Submitted</h2>
              <p className="text-gray-600 mb-6">You have already submitted a proposal for this RFQ.</p>
              <div className="flex gap-4 justify-center">
                <Link 
                  to="/vendor/proposals"
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  View My Proposals
                </Link>
                <Link 
                  to="/enterprise/gov-rfqs"
                  className="px-6 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                >
                  Back to RFQs
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-6">
          <Link 
            to="/enterprise/gov-rfqs"
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to RFQ Listings
          </Link>
          <h1 className="text-2xl font-bold text-gray-900">Respond to RFQ</h1>
        </div>

        {!showForm ? (
          // RFQ Details View
          <div className="space-y-6">
            {/* RFQ Overview */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">{rfq.title}</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="flex items-center gap-3">
                  <Building className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Agency</p>
                    <p className="font-medium">{rfq.GovAgency.name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Location</p>
                    <p className="font-medium">{rfq.location}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <DollarSign className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Budget Range</p>
                    <p className="font-medium">{rfq.budgetRange}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-gray-400" />
                  <div>
                    <p className="text-sm text-gray-500">Deadline</p>
                    <p className="font-medium">{deadlineDate.toLocaleDateString()}</p>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Project Description</h3>
                <div className="prose prose-sm max-w-none">
                  <p className="text-gray-700 whitespace-pre-wrap">{rfq.description}</p>
                </div>
              </div>

              {rfq.attachments && rfq.attachments.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-medium text-gray-900 mb-3">Attachments</h3>
                  <div className="space-y-2">
                    {rfq.attachments.map((attachment, index) => (
                      <a 
                        key={index}
                        href={attachment}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-blue-600 hover:text-blue-700"
                      >
                        <FileText className="w-4 h-4" />
                        Document {index + 1}
                      </a>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <button
                  onClick={() => setShowForm(true)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Submit Proposal
                </button>
              </div>
            </div>

            {/* Agency Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Agency Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Agency Type</p>
                  <p className="font-medium">{rfq.GovAgency.agencyType}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Contact Person</p>
                  <p className="font-medium">{rfq.GovAgency.contactPerson}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="font-medium">{rfq.GovAgency.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="font-medium">{rfq.GovAgency.phone}</p>
                </div>
                <div className="md:col-span-2">
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="font-medium">
                    {rfq.GovAgency.address}, {rfq.GovAgency.city}, {rfq.GovAgency.state} {rfq.GovAgency.zipCode}
                  </p>
                </div>
                {rfq.GovAgency.website && (
                  <div className="md:col-span-2">
                    <p className="text-sm text-gray-500">Website</p>
                    <a 
                      href={rfq.GovAgency.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-medium text-blue-600 hover:text-blue-700"
                    >
                      {rfq.GovAgency.website}
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        ) : (
          // Proposal Submission Form
          <ProposalSubmissionForm
            rfqId={rfq.id}
            rfqTitle={rfq.title}
            agencyName={rfq.GovAgency.name}
            deadline={rfq.deadline}
            onSuccess={handleProposalSuccess}
            onCancel={() => setShowForm(false)}
          />
        )}
      </div>
    </div>
  )
} 